/**
 * @file config.ts
 *
 * Public client configuration, resolved at runtime from `window.__WSB_COMMERCE_CONFIG__`
 * — emitted per environment from the layered `config/*.cjson` (see
 * `scripts/gen-runtime-config.mjs`) and loaded before the bundle. Runtime resolution is
 * required because the SPA is built once and shipped to every environment; the hardcoded
 * fallbacks are a last resort if the config script fails to load. These values are public
 * — never put a secret here.
 */

/** Shape of the runtime config object injected by the deployment. */
interface WsbCommerceRuntimeConfig {
  commerceEngineUrl?: string;
  wsbV2projectUuid?: string;
  useMocks?: boolean;
  allowAllPlans?: boolean;
}

declare global {
  interface Window {
    __WSB_COMMERCE_CONFIG__?: WsbCommerceRuntimeConfig;
  }
}

const runtimeConfig =
  typeof window !== 'undefined' ? window.__WSB_COMMERCE_CONFIG__ : undefined;

/** Base URL of the commerce-engine API. */
export const COMMERCE_ENGINE_URL =
  runtimeConfig?.commerceEngineUrl ?? 'http://localhost:4000';

/**
 * Default `wsbV2projectUuid` prefilled in the password-mode login form so the
 * local/preview flows (project summary, shop bootstrap) resolve the same way
 * they do in token mode. Clearing the field falls back to the user's configured
 * domain. Public, non-secret — same default the legacy login page used.
 */
export const DEFAULT_PROJECT_UUID =
  runtimeConfig?.wsbV2projectUuid ?? 'a1da0c0e-4b1d-4f00-8c0d-000000000001';

/**
 * Whether shop `/shop/*` requests are served by the in-browser MSW fixtures instead of
 * the real commerce-engine. Resolved per environment from the layered cjson config (see
 * `config/*.cjson`); defaults off so staging/production hit the real API.
 */
export const USE_MOCKS = runtimeConfig?.useMocks ?? false;

/**
 * Whether the client plan gate is bypassed so any project plan may enter the admin.
 * Mirrors commerce-engine's `provisioning.allowAllPlans`; resolved per environment from
 * the layered cjson config. Defaults off (fail-closed) so the gate stays active unless a
 * deployment opts in.
 */
export const ALLOW_ALL_PLANS = runtimeConfig?.allowAllPlans ?? false;
